import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '../../contexts/UserContext'
import { useCarControl } from './useCarControl'
import VehiclePicker from './VehiclePicker'
import './index.css'

import carImg from '../../images/carControl/car.png'
import carBgImg from '../../images/carControl/car_bg.png'

/**
 * 控车主页面组件（新设计）
 * 参考司机端UI风格：蓝色渐变背景、车辆展示、分离的解锁/闭锁按钮、支持多车辆切换
 */
export default function CarControl() {
    const navigate = useNavigate()
    const handleBack = () => navigate('/')

    const { userInfo } = useUser()
    const {
        vehicles,
        currentVehicle,
        deviceStatus,
        loading,
        actionLoading,
        error,
        switchVehicle,
        unlock,
        lock,
        findCar,
        mapPicker,
        closeMapPicker,
        openAmapApp,
        openBaiduApp,
        openTencentApp
    } = useCarControl()

    const [pickerVisible, setPickerVisible] = useState(false)
    const [toast, setToast] = useState(null)

    const showToast = (message, duration = 2500) => {
        setToast(message)
        setTimeout(() => setToast(null), duration)
    }

    const handleUnlock = async () => {
        const result = await unlock()
        if (result && result.code === 200) {
            showToast('解锁成功')
        } else if (result) {
            showToast(result.message)
        }
    }

    const handleLock = async () => {
        const result = await lock()
        if (result && result.code === 200) {
            showToast('闭锁成功')
        } else if (result) {
            showToast(result.message)
        }
    }

    const handleFindCar = async () => {
        const result = await findCar()
        if (result) {
            showToast(result.message)
        }
    }

    const handleCallManager = () => {
        if (currentVehicle && currentVehicle.vehicleManagePhone) {
            window.location.href = `tel:${currentVehicle.vehicleManagePhone}`
        }
    }

    // 判断锁状态
    const isLocked = deviceStatus?.lock === 1 || deviceStatus?.lock === '1'
    // 判断设备在线状态
    const deviceOnline = deviceStatus?.online === 1 || deviceStatus?.online === '1'
    // 发动机状态：0-OFF 1-ACC 2-ON 3-Start
    const engineLabels = {0: 'OFF', 1: 'ACC', 2: 'ON', 3: 'Start'}
    const engineClassMap = {0: 'cc-tag-engine-off', 1: 'cc-tag-engine-acc', 2: 'cc-tag-engine-on', 3: 'cc-tag-engine-start'}
    const engineValue = deviceStatus?.engine
    const engineLabel = engineValue != null ? (engineLabels[engineValue] || '未知') : '未知'
    const engineClass = engineClassMap[engineValue] || 'cc-tag-engine-off';
    // 小电压（来自 getVehiclesStatus 的 bat 字段）
    const batVoltage = deviceStatus?.bat

    const avatarUrl = userInfo?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(userInfo?.name || '用户')}&background=random&color=fff`

    if (loading) {
        return (
            <div className="car-control-page">


                <div className="cc-loading">
                    <div className="cc-loading-spinner" />
                    <p>加载中...</p>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="car-control-page">
                <div className="cc-error">
                    <p>{error}</p>
                    <button className="cc-retry-btn" onClick={() => window.location.reload()}>重试</button>
                </div>
            </div>
        )
    }

    // 空状态：无可控车辆
    if (!currentVehicle) {
        return (
            <div className="car-control-page">
                <div className="cc-header-bar">
                    <span className="cc-back-btn" onClick={handleBack}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                    </span>
                    <span className="cc-header-title">我要控车</span>
                    <span className="cc-header-spacer" />
                </div>
                <div className="cc-empty-state">
                    <div className="cc-empty-icon">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 16H9m10 0h3v-3.15a1 1 0 00-.84-.99L16 10l-2.7-3.6a1 1 0 00-.8-.4H5.24a2 2 0 00-1.8 1.1l-.8 1.63A6 6 0 002 12.42V16a2 2 0 002 2h3.34M17 18a2 2 0 11-4 0 2 2 0 014 0z"/><circle cx="7" cy="7" r="5"/></svg>
                    </div>
                    <p className="cc-empty-text">暂无可控车辆，请先提交用车申请</p>
                </div>
            </div>
        )
    }

    return (
        <div className="car-control-page">


            {/* 顶部蓝色渐变区域 — 用 car_bg.png 统一覆盖用户区+车牌+车辆展示 */}
            <div className="cc-blue-header" style={{ backgroundImage: `url(${carBgImg})` }}>
                {/* 顶部导航栏 */}
                <div className="cc-header-bar">
                    <span className="cc-back-btn" onClick={handleBack}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                    </span>
                    <span className="cc-header-title">我要控车</span>
                    <span className="cc-header-spacer" />
                </div>

                {/* 1. 用户区 — 头像 + 用户名/车牌号并排 */}
                <div className="cc-user-section">
                    <img className="cc-avatar" src={avatarUrl} alt="头像" />
                    <div className="cc-user-info-wrap">
                        {/*<div className="cc-user-name">{userName}</div>*/}
                        <div
                            className="cc-plate-bar"
                            onClick={() => vehicles.length > 1 && setPickerVisible(true)}
                            style={{ cursor: vehicles.length > 1 ? 'pointer' : 'default' }}
                        >
                            <span className="cc-plate-number">{currentVehicle.plateNumber}</span>
                            {vehicles.length > 1 && (
                                <svg className="cc-plate-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                            )}
                        </div>

                    </div>
                </div>

                {/* 2. 车辆展示区 */}
                <div className="cc-vehicle-display">
                    <img
                        className="cc-vehicle-image"
                        src={carImg}
                        alt="车辆"
                        onError={(e) => { e.target.src = carImg }}
                    />
                </div>
                {currentVehicle.cityName && (
                    <span className="city-tag">车辆归属地：{currentVehicle.cityName}</span>
                )}
            </div>

            {/* 4. 状态标签行 */}
            <div className="cc-status-tags">
                {/* 小电压 — 在设备在线左边显示 */}
                {batVoltage != null && batVoltage !== '' && (
                    <span className="cc-tag cc-tag-battery">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 18H3a2 2 0 01-2-2V8a2 2 0 012-2h2"/><path d="M9 18h10a2 2 0 002-2V8a2 2 0 00-2-2H9a2 2 0 00-2 2v8a2 2 0 002 2z"/><line x1="13" y1="8" x2="13" y2="12"/><line x1="17" y1="8" x2="17" y2="12"/></svg>
                        {batVoltage}V
                    </span>
                )}
                <span className={`cc-tag ${deviceOnline ? 'cc-tag-online' : 'cc-tag-offline'}`}>
                    <span className={`cc-dot ${deviceOnline ? 'cc-dot-green' : 'cc-dot-offline'}`} />
                    {deviceOnline ? '设备在线' : '设备离线'}
                </span>
                {/*<span className={`cc-tag ${engineClass}`}>
                    <span className={`cc-dot cc-dot-engine-${['off','acc','on','start'][engineValue] || 'off'}`} />
                    ACC: {engineLabel}
                </span>*/}
              {/*  <span className={`cc-tag cc-tag-bluetooth ${deviceOnline ? 'cc-tag-online' : 'cc-tag-offline'}`}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6.5 6.5 17.5 17.5 12 23 12 1 17.5 6.5 6.5 17.5"/></svg>
                    连接蓝牙
                </span>*/}
            </div>

            {/* 5. 操作按钮区（白色卡片） */}
            <div className="cc-action-panel">
                <div className="cc-action-buttons">
                    {/* 闭锁按钮 */}
                    <div className="cc-action-btn-wrapper">
                        <button
                            className={`cc-action-btn cc-btn-lock ${isLocked ? 'cc-btn-active' : ''}`}
                            onClick={handleLock}
                            // G30 修复：移除 !deviceOnline 限制
                            disabled={actionLoading}
                        >
                            {actionLoading ? (
                                <span className="cc-btn-loading" />
                            ) : (
                                <svg className="cc-btn-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                            )}
                        </button>
                        <span className="cc-action-label">闭锁</span>
                    </div>

                    {/* 解锁/已解锁按钮 */}
                    <div className="cc-action-btn-wrapper">
                        <button
                            className={`cc-action-btn cc-btn-unlock ${!isLocked ? 'cc-btn-active' : ''}`}
                            onClick={handleUnlock}
                            // G30 修复：移除 !deviceOnline 限制，让用户始终可以点击（后端会处理离线错误）
                            disabled={actionLoading}
                        >
                            {actionLoading ? (
                                <span className="cc-btn-loading" />
                            ) : (
                                <svg className="cc-btn-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/><circle cx="12" cy="16" r="1" fill="currentColor"/></svg>
                            )}
                        </button>
                        <span className={`cc-action-label ${!isLocked ? 'cc-label-active' : ''}`}>
                            {!isLocked ? '已解锁' : '解锁'}
                        </span>
                    </div>

                    {/* 寻车 */}
                    <div className="cc-action-btn-wrapper">
                        <button
                            className="cc-action-btn cc-btn-find"
                            onClick={handleFindCar}
                            disabled={actionLoading}
                        >
                            {actionLoading ? (
                                <span className="cc-btn-loading" />
                            ) : (
                                <svg className="cc-btn-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 16H9m10 0h3v-3.15a1 1 0 00-.84-.99L16 10l-2.7-3.6a1 1 0 00-.8-.4H5.24a2 2 0 00-1.8 1.1l-.8 1.63A6 6 0 002 12.42V16a2 2 0 002 2h3.34"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>
                            )}
                        </button>
                        <span className="cc-action-label">位置</span>
                    </div>
                </div>
            </div>

            {/* 6. 底部信息区 */}
            <div className="cc-bottom-info">
                {currentVehicle.vehicleManagePhone && (
                    <div className="cc-info-row" onClick={handleCallManager}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1e88e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                        <span className="cc-info-label">车管电话:</span>
                        <span className="cc-info-value cc-info-link">{currentVehicle.vehicleManagePhone}</span>
                    </div>
                )}
                <div className="cc-info-row">
                    <span className="cc-info-label">数字钥匙设备ID:</span>
                    <span className="cc-info-value">{currentVehicle.deviceId}</span>
                </div>
            </div>

            {/* 车辆选择器 */}
            <VehiclePicker
                vehicles={vehicles}
                visible={pickerVisible}
                onSelect={switchVehicle}
                onClose={() => setPickerVisible(false)}
            />

            {/* Toast 提示 */}
            {toast && (
                <div className="cc-toast">{toast}</div>
            )}

            {/* 地图选择弹窗（寻车：高德/百度/腾讯，各自对应坐标系已转换） */}
            {mapPicker && (
                <div className="cc-map-picker-overlay" onClick={closeMapPicker}>
                    <div className="cc-map-picker" onClick={(e) => e.stopPropagation()}>
                        <div className="cc-map-picker-title">选择地图导航</div>
                        <div className="cc-map-picker-item" onClick={() => openAmapApp(mapPicker)}>
                            <span className="cc-map-icon cc-map-icon-amap">A</span>
                            <span className="cc-map-name">高德地图</span>
                            <span className="cc-map-arrow">›</span>
                        </div>
                        <div className="cc-map-picker-item" onClick={() => openBaiduApp(mapPicker)}>
                            <span className="cc-map-icon cc-map-icon-baidu">B</span>
                            <span className="cc-map-name">百度地图</span>
                            <span className="cc-map-arrow">›</span>
                        </div>
                        <div className="cc-map-picker-item" onClick={() => openTencentApp(mapPicker)}>
                            <span className="cc-map-icon cc-map-icon-tencent">T</span>
                            <span className="cc-map-name">腾讯地图</span>
                            <span className="cc-map-arrow">›</span>
                        </div>
                        <div className="cc-map-picker-cancel" onClick={closeMapPicker}>取消</div>
                    </div>
                </div>
            )}
        </div>
    )
}
